import React, { useState, useEffect } from 'react';
import { Input, Button, Alert } from '../common';
import { BookOpen, Tag, FileText, Link as LinkIcon } from 'lucide-react';

const GlosarioForm = ({
  termino = null,
  onSubmit,
  onCancel,
  loading = false,
  error = null,
}) => {
  const isEdit = !!termino;

  const [formData, setFormData] = useState({
    termino: '',
    definicion: '',
    categoria: '',
    ejemplo: '',
    referencias: '',
  });

  const [formErrors, setFormErrors] = useState({});

  // Categorías disponibles
  const categorias = [
    'Ética General',
    'Ética Profesional',
    'Bioética',
    'Ética Digital',
    'Derechos Humanos',
    'Responsabilidad Social',
    'Integridad',
    'Transparencia',
    'Compliance',
    'Otros',
  ];

  // Cargar datos si es edición
  useEffect(() => {
    if (termino) {
      setFormData({
        termino: termino.termino || '',
        definicion: termino.definicion || '',
        categoria: termino.categoria || '',
        ejemplo: termino.ejemplo || '',
        referencias: termino.referencias || '',
      });
    }
  }, [termino]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });
    // Limpiar error del campo
    if (formErrors[name]) {
      setFormErrors({
        ...formErrors,
        [name]: '',
      });
    }
  };

  const validateForm = () => {
    const errors = {};

    if (!formData.termino.trim()) {
      errors.termino = 'El término es requerido';
    }

    if (!formData.definicion.trim()) {
      errors.definicion = 'La definición es requerida';
    } else if (formData.definicion.trim().length < 20) {
      errors.definicion = 'La definición debe tener al menos 20 caracteres';
    }

    if (!formData.categoria) {
      errors.categoria = 'La categoría es requerida';
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    // Preparar datos
    const submitData = {
      termino: formData.termino.trim(),
      definicion: formData.definicion.trim(),
      categoria: formData.categoria,
    };

    // Agregar campos opcionales si tienen valor
    if (formData.ejemplo.trim()) {
      submitData.ejemplo = formData.ejemplo.trim();
    }

    if (formData.referencias.trim()) {
      submitData.referencias = formData.referencias.trim();
    }

    onSubmit(submitData);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {error && <Alert type="error" message={error} />}

      <Input
        label="Término"
        name="termino"
        type="text"
        placeholder="Ej: Confidencialidad"
        value={formData.termino}
        onChange={handleChange}
        error={formErrors.termino}
        icon={BookOpen}
        required
        disabled={loading}
      />

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Categoría <span className="text-red-500">*</span>
        </label>
        <div className="relative">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <Tag className="h-5 w-5 text-gray-400" />
          </div>
          <select
            name="categoria"
            value={formData.categoria}
            onChange={handleChange}
            disabled={loading}
            className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
          >
            <option value="">Selecciona una categoría</option>
            {categorias.map((cat) => (
              <option key={cat} value={cat}>
                {cat}
              </option>
            ))}
          </select>
        </div>
        {formErrors.categoria && (
          <p className="mt-1 text-sm text-red-600">{formErrors.categoria}</p>
        )}
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Definición <span className="text-red-500">*</span>
        </label>
        <textarea
          name="definicion"
          value={formData.definicion}
          onChange={handleChange}
          placeholder="Escribe la definición del término..."
          rows={4}
          disabled={loading}
          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-primary-500 sm:text-sm resize-none"
        />
        {formErrors.definicion && (
          <p className="mt-1 text-sm text-red-600">{formErrors.definicion}</p>
        )}
        <p className="mt-1 text-xs text-gray-500">
          Mínimo 20 caracteres. Actual: {formData.definicion.length}
        </p>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Ejemplo de Uso
        </label>
        <div className="relative">
          <div className="absolute top-3 left-3 pointer-events-none">
            <FileText className="h-5 w-5 text-gray-400" />
          </div>
          <textarea
            name="ejemplo"
            value={formData.ejemplo}
            onChange={handleChange}
            placeholder="Ej: 'La confidencialidad médica protege la información del paciente...'"
            rows={3}
            disabled={loading}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-primary-500 sm:text-sm resize-none"
          />
        </div>
        <p className="mt-1 text-xs text-gray-500">
          Opcional. Un ejemplo práctico ayuda a entender el término.
        </p>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Referencias
        </label>
        <div className="relative">
          <div className="absolute top-3 left-3 pointer-events-none">
            <LinkIcon className="h-5 w-5 text-gray-400" />
          </div>
          <textarea
            name="referencias"
            value={formData.referencias}
            onChange={handleChange}
            placeholder="Ej: 'Código de Ética Profesional, Artículo 5...'"
            rows={2}
            disabled={loading}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-primary-500 sm:text-sm resize-none"
          />
        </div>
        <p className="mt-1 text-xs text-gray-500">
          Opcional. Fuentes, artículos o documentos de referencia.
        </p>
      </div>

      <div className="flex justify-end gap-3 pt-4">
        <Button type="button" variant="secondary" onClick={onCancel} disabled={loading}>
          Cancelar
        </Button>
        <Button type="submit" variant="primary" loading={loading}>
          {isEdit ? 'Actualizar Término' : 'Crear Término'}
        </Button>
      </div>
    </form>
  );
};

export default GlosarioForm;
